import apiService from "src/services/ApiCallService";
import { useEffect, useState } from "react";
import ErrorUtils from "src/utils/ErrorUtils";
import { ListSegmentDataTablesRequest, ListSegmentDataTablesResponse } from "src/models/segment-cdt/SegmentCdtTypes";

/**
 * Service for Segment-Level Custom Data Tables. One place for every segment-CDT operation; CR1 adds
 * the list read, later CRs add create/get/search/pull/etc. Hooks follow the house
 * `[result, loading, error]` shape used across ATP services (see CustomDataTablesService).
 */
export default class SegmentDataTablesService {

    /**
     * Lists segment data tables. Backed by `ListSegmentDataTables` (POST /list/segment-data-tables).
     * That server op is not deployed yet, so this returns no rows today. The whole Segment tab is
     * gated behind `segmentCdtEnabled` (off in launched stages), so the empty/erroring list is never
     * user-visible; it populates on its own once the backend ships. Returns the raw contract
     * response; the caller maps `tables` to display rows.
     * @param request optional segmentType / tableType filters (both omitted => all)
     */
    listSegmentDataTables(request?: ListSegmentDataTablesRequest): [ListSegmentDataTablesResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as ListSegmentDataTablesResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                setLoading(true);
                // Reset any prior error: this hook refetches on a refreshToken bump (after Create),
                // so a success following a failure must clear the stale error, and a recurring
                // identical error must still re-fire the view's useEffect([error]) banner.
                setError('');
                try {
                    const response = await apiService.listSegmentDataTables(request);
                    const json = await response.json() as ListSegmentDataTablesResponse;
                    setResult(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (request != null) {
                fetchData();
            }
        }, [request]);

        return [result, loading, error];
    }
}
